import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Button } from '../ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Input } from '../ui/Input';
import {
  InformationCircleIcon,
  ArrowUturnLeftIcon,
  PencilSquareIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import apiClient from '../../utils/api';

// Formats a value with its unit ("8h 24m", "3,259 steps", "94.4%")
const formatValue = (v, unit) => {
  if (v === null || v === undefined) return '—';
  if (unit === 'min') {
    const h = Math.floor(v / 60);
    const m = Math.round(v % 60);
    return h > 0 ? (m > 0 ? `${h}h ${m}m` : `${h}h`) : `${m}m`;
  }
  const num = Number.isInteger(v) ? v.toLocaleString() : v.toLocaleString(undefined, { maximumFractionDigits: 1 });
  return unit === '%' || unit === '/10' ? `${num}${unit}` : `${num} ${unit}`;
};

// Bare number for compact labels — units already sit elsewhere on the line
const formatShort = (v, unit) => {
  if (v === null || v === undefined) return '—';
  if (unit === 'min') return formatValue(v, unit);
  return Number.isInteger(v) ? v.toLocaleString() : v.toLocaleString(undefined, { maximumFractionDigits: 1 });
};

// "7–9h" / "≥ 8,000 steps" / "≤ 100 bpm"
const formatRange = (low, high, unit) => {
  if (low !== null && high !== null) {
    if (unit === 'min') return `${formatValue(low, unit)} – ${formatValue(high, unit)}`;
    const lo = Number.isInteger(low) ? low.toLocaleString() : low.toLocaleString(undefined, { maximumFractionDigits: 1 });
    return `${lo} – ${formatValue(high, unit)}`;
  }
  if (low !== null) return `≥ ${formatValue(low, unit)}`;
  if (high !== null) return `≤ ${formatValue(high, unit)}`;
  return null;
};

// Is the current baseline already inside the target band (direction-aware)?
const isInBand = (t) => {
  if (t.baseline === null) return false;
  if (t.direction === 'up') return t.ultimate_low !== null && t.baseline >= t.ultimate_low;
  if (t.direction === 'down') return t.ultimate_high !== null && t.baseline <= t.ultimate_high;
  return (
    t.ultimate_low !== null && t.ultimate_high !== null &&
    t.baseline >= t.ultimate_low && t.baseline <= t.ultimate_high
  );
};

// The band edge the user is walking toward (null when in band / no band)
const targetEdge = (t) => {
  if (t.baseline === null) return null;
  if (t.direction === 'up') {
    return t.ultimate_low !== null && t.baseline < t.ultimate_low ? t.ultimate_low : null;
  }
  if (t.direction === 'down') {
    return t.ultimate_high !== null && t.baseline > t.ultimate_high ? t.ultimate_high : null;
  }
  if (t.ultimate_low !== null && t.baseline < t.ultimate_low) return t.ultimate_low;
  if (t.ultimate_high !== null && t.baseline > t.ultimate_high) return t.ultimate_high;
  return null;
};

const FIELD_LABELS = { age: 'age', sex: 'sex', height_cm: 'height' };

// Who decided this target? Short tag shown under the target value.
const provenance = (t) => {
  if (t.ultimate_is_custom) {
    return { text: 'set by you', chip: 'bg-primary-100 text-primary-800 dark:bg-primary-900/50 dark:text-primary-300' };
  }
  const tier = t.seed_tier === 'standard' ? 'standard' : 'optimal';
  if (t.seed_source === 'generic') {
    return {
      text: `${tier} · generic`,
      chip: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
    };
  }
  return {
    text: t.seed_source === 'personalized' ? `${tier} · tuned to you` : tier,
    chip: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  };
};

// Position on the journey track: 0 = furthest from target, 1 = at target
const trackPos = (v, worst, edge) => {
  if (v === null || edge === null || worst === edge) return 1;
  return Math.min(1, Math.max(0, 1 - Math.abs(v - edge) / Math.abs(worst - edge)));
};

// Nudge label positions apart so words never overlap (dots keep true pos)
const spreadLabels = (positions) => {
  const MIN = 0.16;
  const out = [...positions];
  for (let i = 1; i < out.length; i++) out[i] = Math.max(out[i], out[i - 1] + MIN);
  if (out[out.length - 1] > 1) {
    out[out.length - 1] = 1;
    for (let i = out.length - 2; i >= 0; i--) out[i] = Math.min(out[i], out[i + 1] - MIN);
  }
  return out;
};

const dotLeft = (p) => `${(2 + p * 95).toFixed(1)}%`;

const TargetRow = ({ target, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [low, setLow] = useState(target.ultimate_low ?? '');
  const [high, setHigh] = useState(target.ultimate_high ?? '');
  const [showWhy, setShowWhy] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLow(target.ultimate_low ?? '');
    setHigh(target.ultimate_high ?? '');
  }, [target.ultimate_low, target.ultimate_high]);

  const patch = async (body, successMsg) => {
    setSaving(true);
    try {
      const response = await apiClient.patch(`/api/twin/targets/${target.metric_key}`, body);
      onSaved(response.data);
      if (successMsg) toast.success(successMsg);
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update target');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    const ok = await patch(
      {
        ultimate_low: low === '' ? null : parseFloat(low),
        ultimate_high: high === '' ? null : parseFloat(high),
      },
      `${target.label} target updated`
    );
    if (ok) setEditing(false);
  };

  const handleTier = (tier) =>
    patch({ seed_tier: tier }, `${target.label} now aims at the ${tier} range`);

  const handleToggle = () => patch({ enabled: !target.enabled });

  const handleReset = async () => {
    setSaving(true);
    try {
      const response = await apiClient.post(`/api/twin/targets/${target.metric_key}/reset`);
      onSaved(response.data);
      toast.success(`${target.label} reset to reference range`);
      setEditing(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset target');
    } finally {
      setSaving(false);
    }
  };

  const prov = provenance(target);
  const inBand = isInBand(target);
  const targetText = formatRange(target.ultimate_low, target.ultimate_high, target.unit);
  const edge = targetEdge(target);
  const hasNextStep =
    !inBand && target.next_value !== null && target.baseline !== null &&
    target.next_value !== target.baseline;

  // Journey positions: track runs from the worse of (start, now) to the target
  const start = target.start_value;
  let marks = null;
  if (!inBand && edge !== null && target.baseline !== null) {
    const candidates = start !== null ? [start, target.baseline] : [target.baseline];
    const worst = candidates.reduce((a, b) => (Math.abs(a - edge) >= Math.abs(b - edge) ? a : b));
    marks = [];
    const posNow = trackPos(target.baseline, worst, edge);
    const posStart = start !== null ? trackPos(start, worst, edge) : null;
    if (posStart !== null && Math.abs(posStart - posNow) >= 0.05) {
      marks.push({ key: 'start', pos: posStart, label: 'start', title: `Started: ${formatValue(start, target.unit)}` });
    }
    marks.push({ key: 'now', pos: posNow, label: 'now', title: `Now: ${formatValue(target.baseline, target.unit)}` });
    if (hasNextStep) {
      marks.push({
        key: 'next',
        pos: trackPos(target.next_value, worst, edge),
        label: 'next',
        title: `Next step: ${formatValue(target.next_value, target.unit)}`,
      });
    }
    marks.push({ key: 'target', pos: 1, label: 'target', title: `Target: ${targetText}` });
    marks.sort((a, b) => a.pos - b.pos);
    const labelPos = spreadLabels(marks.map((m) => m.pos));
    marks = marks.map((m, i) => ({ ...m, labelPos: labelPos[i] }));
  }

  const markStyle = {
    start: 'w-2.5 h-2.5 rounded-full border-2 border-gray-400 dark:border-gray-500 bg-white dark:bg-gray-900',
    now: 'w-3.5 h-3.5 rounded-full bg-gray-900 dark:bg-white',
    next: 'w-3 h-3 rounded-full bg-primary-500',
    target: 'w-1.5 h-4 rounded-sm bg-green-600 dark:bg-green-500',
  };
  const labelStyle = {
    start: 'text-gray-600 dark:text-gray-300',
    now: 'text-gray-900 dark:text-white font-semibold',
    next: 'text-primary-600 dark:text-primary-400 font-medium',
    target: 'text-green-700 dark:text-green-400 font-semibold',
  };

  const tierButton = (tier, lowV, highV, citation) => {
    const active = !target.ultimate_is_custom && target.seed_tier === tier;
    return (
      <button
        type="button"
        disabled={saving || active}
        onClick={() => handleTier(tier)}
        className={`text-left px-3 py-2 rounded-md border text-sm transition-colors ${
          active
            ? 'border-primary-600 bg-primary-50 dark:bg-primary-900/30 text-primary-800 dark:text-primary-200'
            : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
        }`}
        title={citation}
      >
        <span className="font-semibold capitalize">{tier}</span>
        {': '}
        <span className="font-semibold">{formatRange(lowV, highV, target.unit) || '—'}</span>
        <span className="block text-sm text-gray-600 dark:text-gray-300">
          {tier === 'optimal' ? 'top-tier health' : 'clinical normal'}
          {active ? ' · current aim' : ''}
        </span>
      </button>
    );
  };

  // The "average · optimal · yours" context strip
  const optimalText = formatRange(target.optimal_low, target.optimal_high, target.unit);
  const strip = [];
  if (target.average_value !== null) {
    strip.push(
      <span key="avg" title={target.average_citation}>
        average person <span className="font-semibold text-gray-700 dark:text-gray-200">{formatValue(target.average_value, target.unit)}</span>
      </span>
    );
  }
  if (optimalText) {
    strip.push(
      <span key="opt" title={target.optimal_citation}>
        optimal <span className="font-semibold text-green-700 dark:text-green-400">{optimalText}</span>
      </span>
    );
  }
  if (target.ultimate_is_custom && targetText) {
    strip.push(
      <span key="you">
        your target <span className="font-semibold text-primary-700 dark:text-primary-300">{targetText}</span>
      </span>
    );
  }

  return (
    <div
      className={`py-5 border-b border-gray-200 dark:border-gray-700 last:border-b-0 ${
        target.enabled ? '' : 'opacity-50'
      } ${inBand && target.enabled ? 'border-l-4 border-l-green-500 dark:border-l-green-600 pl-3 -ml-3 sm:pl-4 sm:-ml-4' : ''}`}
    >
      {/* Header: name + citation + on/off */}
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <p className="text-base font-semibold text-gray-900 dark:text-white">{target.label}</p>
          <button
            type="button"
            onClick={() => setShowWhy((s) => !s)}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 flex-shrink-0"
            title="Why these ranges?"
          >
            <InformationCircleIcon className="w-5 h-5" />
          </button>
        </div>
        <button
          type="button"
          onClick={handleToggle}
          disabled={saving}
          role="switch"
          aria-checked={target.enabled}
          className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
            target.enabled ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-600'
          }`}
          title={target.enabled ? 'Tracking on — tap to opt out' : 'Opted out — tap to track'}
        >
          <span
            className={`inline-block h-[18px] w-[18px] transform rounded-full bg-white transition-transform ${
              target.enabled ? 'translate-x-[22px]' : 'translate-x-0.5'
            }`}
          />
        </button>
      </div>

      {/* Why these ranges: both tiers + population average, with citations */}
      {showWhy && (
        <div className="mb-3 text-sm text-gray-600 dark:text-gray-400 space-y-1.5 bg-gray-50 dark:bg-gray-800 rounded-md px-3 py-2">
          {target.tiers_coincide ? (
            <p>
              <span className="font-semibold">{formatRange(target.optimal_low, target.optimal_high, target.unit) || 'No range yet'}</span>
              {' — '}{target.optimal_citation} <span className="italic">(standard and optimal coincide here)</span>
            </p>
          ) : (
            <>
              <p>
                <span className="font-semibold text-green-700 dark:text-green-400">Optimal {formatRange(target.optimal_low, target.optimal_high, target.unit) || '—'}</span>
                {' — '}{target.optimal_citation}
              </p>
              <p>
                <span className="font-semibold">Standard {formatRange(target.standard_low, target.standard_high, target.unit) || '—'}</span>
                {' — '}{target.standard_citation}
              </p>
            </>
          )}
          {target.average_citation && (
            <p>
              <span className="font-semibold">Average person{target.average_value !== null ? ` ${formatValue(target.average_value, target.unit)}` : ''}</span>
              {' — '}{target.average_citation}
            </p>
          )}
        </div>
      )}

      {target.enabled && (
        <>
          {/* Numbers: where you are | where you're going (tap target to edit) */}
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300 mb-0.5">
                You now
              </p>
              {target.baseline !== null ? (
                <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white whitespace-nowrap">
                  {formatShort(target.baseline, target.unit)}
                  {(target.unit === '%' || target.unit === '/10') && <span className="text-sm font-medium text-gray-600 dark:text-gray-300">{target.unit}</span>}
                </p>
              ) : (
                <p className="text-base font-medium text-gray-600 dark:text-gray-300 whitespace-nowrap">No data yet</p>
              )}
              {start !== null && target.baseline !== null && (
                <p className="text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                  started {formatShort(start, target.unit)}
                  {target.start_weeks_ago ? ` · ${target.start_weeks_ago}w ago` : ''}
                </p>
              )}
            </div>

            <button
              type="button"
              onClick={() => setEditing((e) => !e)}
              className="text-right rounded-lg px-2 py-1 -mr-2 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors group"
              title="Tap to adjust your target"
            >
              <p className="text-sm font-semibold uppercase tracking-wider text-green-700 dark:text-green-400 mb-0.5 flex items-center justify-end gap-1">
                Your target
                <PencilSquareIcon className="w-4 h-4 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300" />
              </p>
              {targetText ? (
                <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white whitespace-nowrap">{targetText}</p>
              ) : (
                <p className="text-base font-medium text-gray-600 dark:text-gray-300">Tap to set</p>
              )}
              <span className={`inline-block mt-1 text-sm px-2 py-0.5 rounded-full ${prov.chip}`}>
                {prov.text}
              </span>
            </button>
          </div>

          {/* The path — full-width timeline, every mark labeled in place */}
          {inBand ? (
            <div className="mt-1 flex items-center gap-1.5 text-green-700 dark:text-green-400">
              <CheckCircleIcon className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm font-medium">in your target range</span>
            </div>
          ) : marks ? (
            <div className="mt-2">
              {hasNextStep && (
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-1 text-center">
                  next step: <span className="font-semibold text-primary-700 dark:text-primary-300 whitespace-nowrap">{formatShort(target.next_value, target.unit)}</span>
                  {target.next_horizon_weeks ? <span className="whitespace-nowrap"> in {target.next_horizon_weeks} weeks</span> : null}
                </p>
              )}
              <div className="relative w-full h-10">
                <div className="absolute inset-x-0 top-[8px] border-t-2 border-dashed border-gray-300 dark:border-gray-600" />
                {marks.map((m) => (
                  <React.Fragment key={m.key}>
                    <div
                      className={`absolute top-[9px] -translate-y-1/2 -translate-x-1/2 ${markStyle[m.key]}`}
                      style={{ left: dotLeft(m.pos) }}
                      title={m.title}
                    />
                    <span
                      className={`absolute top-[20px] -translate-x-1/2 text-sm ${labelStyle[m.key]}`}
                      style={{ left: dotLeft(m.labelPos) }}
                      title={m.title}
                    >
                      {m.label}
                    </span>
                  </React.Fragment>
                ))}
              </div>
            </div>
          ) : null}

          {/* Context: what the average person measures vs optimal vs your choice */}
          {strip.length > 0 && (
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
              {strip.map((s, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <span className="mx-1.5 text-gray-300 dark:text-gray-600">·</span>}
                  {s}
                </React.Fragment>
              ))}
            </p>
          )}

          {target.ultimate_is_custom && !editing && (
            <div className="mt-2">
              <Button variant="outline" size="sm" onClick={handleReset} disabled={saving} className="flex items-center">
                <ArrowUturnLeftIcon className="w-4 h-4 mr-1.5" />
                Back to reference
              </Button>
            </div>
          )}

          {/* Edit panel */}
          {editing && (
            <div className="mt-3 p-4 rounded-lg bg-gray-50 dark:bg-gray-800 space-y-4">
              {!target.tiers_coincide ? (
                <div>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Aim at a reference range:</p>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {tierButton('optimal', target.optimal_low, target.optimal_high, target.optimal_citation)}
                    {tierButton('standard', target.standard_low, target.standard_high, target.standard_citation)}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  For this metric the standard and optimal ranges are the same — adequate is optimal.
                </p>
              )}
              <div className="flex flex-wrap items-end gap-3">
                <div className="w-32">
                  <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1">Low ({target.unit})</label>
                  <Input
                    type="number"
                    value={low}
                    onChange={(e) => setLow(e.target.value)}
                    min={target.clamp_low}
                    max={target.clamp_high}
                    step="any"
                  />
                </div>
                <div className="w-32">
                  <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1">High ({target.unit})</label>
                  <Input
                    type="number"
                    value={high}
                    onChange={(e) => setHigh(e.target.value)}
                    min={target.clamp_low}
                    max={target.clamp_high}
                    step="any"
                  />
                </div>
                <Button size="sm" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : 'Save custom'}
                </Button>
                {target.ultimate_is_custom && (
                  <Button variant="outline" size="sm" onClick={handleReset} disabled={saving} className="flex items-center">
                    <ArrowUturnLeftIcon className="w-4 h-4 mr-1.5" />
                    Back to reference
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={() => setEditing(false)} disabled={saving}>
                  Close
                </Button>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Set low and high to the same number for a single-point target. Your choice always wins —
                out-of-range targets are noted for context, never blocked.
              </p>
            </div>
          )}

          {target.annotation && (
            <p className="mt-3 text-sm text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 rounded-md px-3 py-2">
              {target.annotation}
            </p>
          )}
        </>
      )}
    </div>
  );
};

const TargetsEditor = () => {
  const [targets, setTargets] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchTargets = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/twin/targets');
      setTargets(response.data);
    } catch (error) {
      toast.error('Failed to load twin targets');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTargets();
  }, [fetchTargets]);

  const handleSaved = (updated) => {
    setTargets((prev) =>
      prev.map((t) => (t.metric_key === updated.metric_key ? updated : t))
    );
  };

  const missingFields = targets
    ? [...new Set(
        targets
          .filter((t) => !t.ultimate_is_custom && t.seed_source === 'generic')
          .flatMap((t) => t.seed_missing_inputs.map((f) => FIELD_LABELS[f] || f))
      )]
    : [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Ultimate Targets</CardTitle>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Each line reads left to right: where you started and are today, the next
          reachable step, and where you're headed. Targets default to the{' '}
          <span className="font-semibold text-green-700 dark:text-green-400">optimal</span> range — the one tied to
          top-tier health for your profile — not the merely "normal" clinical range,
          because the average person isn't a healthy benchmark. Tap any target to
          switch it to standard or set your own. Wellness guidance, not medical advice.
        </p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : !targets ? (
          <p className="text-sm text-gray-600 dark:text-gray-300 py-4">Could not load targets.</p>
        ) : (
          <>
            {missingFields.length > 0 && (
              <div className="mb-2 text-sm text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 rounded-md px-3 py-2">
                Some reference ranges are for a generic adult. Add your{' '}
                <span className="font-semibold">{missingFields.join(', ')}</span> in the
                Personal Health Profile above and they'll tune themselves to you.
              </div>
            )}
            <div>
              {targets.map((t) => (
                <TargetRow key={t.metric_key} target={t} onSaved={handleSaved} />
              ))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default TargetsEditor;
